import { useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ConnectableProvider } from '@cowbell/moo';
import { askMoo } from '../../../queries/ask-moo';
import {
  deleteUserConnection,
  setUserConnectionEnabled,
  startUserConnectionAuthorize,
} from '../api';
import { getData } from '../../../utils/functional/fp';
import { beginConnectReturn, type ConnectReturn } from '../v2/connect-return';

/** The auth method the backend uses for per-user (rather than per-tenant) OAuth. */
const USER_OAUTH_METHOD = 'oauth2_user';

/** The calling user's own OAuth connections. */
export function useUserConnections() {
  return useQuery(askMoo.userConnections());
}

/**
 * The rows a user can act on, as the three-way join described in the design:
 * catalog (display metadata) x tenant-enabled integrations (has the org allowed
 * it?) x this user's connections (has THIS user connected it?).
 *
 * A provider the tenant has not enabled as `oauth2_user`/`active` is omitted
 * entirely — the org has not made it available.
 */
export function useConnectableProviders() {
  const catalogQuery = useQuery(askMoo.integrationCatalog());
  const integrationsQuery = useQuery(askMoo.integrations());
  const connectionsQuery = useUserConnections();

  const isSuccess =
    catalogQuery.isSuccess &&
    integrationsQuery.isSuccess &&
    connectionsQuery.isSuccess;
  const isLoading =
    catalogQuery.isLoading ||
    integrationsQuery.isLoading ||
    connectionsQuery.isLoading;
  const isError =
    catalogQuery.isError ||
    integrationsQuery.isError ||
    connectionsQuery.isError;

  let data: ConnectableProvider[] | undefined;

  if (isSuccess) {
    // `askMoo.integrations()` already unwraps to the array.
    const enabledProviders = new Set(
      (integrationsQuery.data ?? [])
        .filter(
          (integration) =>
            // Two ways a row permits user connections: the provider is
            // inherently per-user (no workspace credential exists), or an admin
            // switched per-user sign-in on for it. Matching only the first hid
            // Slack, whose auth_method is bot_token — mirrors the backend's
            // _permits_user_connections gate.
            (integration.auth_method === USER_OAUTH_METHOD ||
              Boolean(integration.user_oauth_enabled)) &&
            integration.status === 'active'
        )
        .map((integration) => integration.provider)
    );

    const connectionByProvider = new Map(
      (connectionsQuery.data ?? []).map((connection) => [
        connection.provider,
        connection,
      ])
    );

    data = (catalogQuery.data ?? [])
      .filter((entry) => enabledProviders.has(entry.provider))
      .map((entry) => {
        const connection = connectionByProvider.get(entry.provider);
        return {
          provider: entry.provider,
          displayName: entry.display_name,
          logoUrl: entry.logo_url,
          connected: Boolean(connection),
          status: connection?.status ?? null,
          externalAccountLabel: connection?.external_account_label ?? null,
          // `null` when the backend doesn't report the field yet, which the UI
          // reads as "no mute support" and hides the control.
          enabled: connection?.enabled ?? null,
        };
      });
  }

  return { data, isSuccess, isLoading, isError };
}

/**
 * Starts the OAuth flow. The POST returns the vendor's real consent-screen URL;
 * we navigate to it rather than constructing one ourselves.
 *
 * Every caller sits on a different surface — the composer chip, the settings
 * modal, an in-chat prompt card, Moo Studio's My Apps page — and each expects
 * to be put back where it was afterwards. Rather than making all four say so,
 * the current route is read here, which is correct for all of them: it is the
 * page the user was looking at when they pressed Connect.
 *
 * Returns a `cancel()` alongside the mutation because the redirect happens in
 * `onSuccess`, after the request resolves. A user who clicks Connect and then
 * backs out would otherwise still be thrown at the vendor's consent screen
 * when the in-flight POST lands. Callers that can be dismissed mid-flight
 * (the in-chat card) must call it; `reset()` would not help, since it does not
 * stop `onSuccess` from running.
 */
export function useStartConnectionAuthorize() {
  // Per-attempt token rather than a boolean. `cancel()` also calls `reset()`,
  // which frees the user to start a second authorize while the first POST is
  // still in flight — and a shared boolean would be cleared by that second
  // attempt, letting the abandoned first response redirect anyway. Comparing
  // the id captured at send time against the live one keeps each attempt's
  // outcome tied to itself.
  const attemptId = useRef(0);
  const liveAttemptId = useRef(0);
  // The latest attempt's marker handle, so cancelling can undo exactly what
  // that attempt recorded — and nothing another Connect button has since
  // recorded. See `ConnectReturn.abandon`.
  const connectReturn = useRef<ConnectReturn | null>(null);

  const mutation = useMutation({
    mutationFn: (provider: string) => {
      attemptId.current += 1;
      liveAttemptId.current = attemptId.current;
      const thisAttempt = attemptId.current;
      const started = beginConnectReturn();
      connectReturn.current = started;
      return startUserConnectionAuthorize(provider, {
        data: { return_path: started.returnPath },
      })
        .then(getData)
        .then((data) => ({ data, thisAttempt }))
        .catch((error) => {
          // A failed POST means no redirect, so nothing should be waiting for
          // the user on arrival — and the composer chip and Apps pane have no
          // cancel path, so this is their only cleanup. `abandon` no-ops if
          // another attempt has since recorded its own markers, whether from
          // this hook instance or from a sibling Connect button.
          started.abandon();
          throw error;
        });
    },
    onSuccess: ({ data, thisAttempt }) => {
      // Superseded or cancelled — do not navigate.
      if (thisAttempt !== liveAttemptId.current) return;
      window.location.href = data.authorize_url;
    },
  });

  const { reset } = mutation;
  const cancel = useCallback(() => {
    // Invalidates every in-flight attempt; the next mutate() takes a new id.
    liveAttemptId.current = -1;
    // The user is not going anywhere, so the recorded route and the pending
    // "reopen the panel on arrival" flag are both stale.
    connectReturn.current?.abandon();
    // Also clear the pending state, or `isLoading` stays true until the
    // abandoned POST settles and a retry shows a stuck Connect button.
    reset();
  }, [reset]);

  return { ...mutation, cancel };
}

/**
 * Mutes/unmutes a connection without revoking it.
 *
 * The stored preference is deliberately independent of connection health, so a
 * token expiring never overwrites what the user chose — reconnecting restores
 * their setting rather than silently leaving the app off.
 */
export function useSetConnectionEnabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      provider,
      enabled,
    }: {
      provider: string;
      enabled: boolean;
    }) =>
      setUserConnectionEnabled(provider, { data: { enabled } }).then(getData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askMoo.userConnections().queryKey,
      });
    },
  });
}

export function useDeleteUserConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (provider: string) =>
      deleteUserConnection(provider).then(getData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askMoo.userConnections().queryKey,
      });
    },
  });
}
